import { inject, Injectable, LOCALE_ID, signal } from '@angular/core';
import { DateRange } from '@shared/models/common.models';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { USER_LOCALE_DATE_TIME_FORMAT } from './user-locale.constants';

@Injectable({
  providedIn: 'root',
})
export class UserLocaleService {
  private readonly DEFAULT_LOCALE = inject(LOCALE_ID);

  private readonly _userLocale = signal<string>(this.DEFAULT_LOCALE);
  public userLocale = this._userLocale.asReadonly();

  private readonly _userTimezone = signal<string>('UTC');
  public userTimezone = this._userTimezone.asReadonly();

  private readonly _userZoneOffset = signal<string>('Z');
  public userZoneOffset = this._userZoneOffset.asReadonly();

  public static readonly SUPPORTED_LOCALES = ['en', 'de', 'nl', 'fr', 'es', 'pt', 'da', 'pl', 'ar', 'tr', 'nb', 'zh', 'pt'];

  public static readonly LOCALE_IMPORT_MAP: Record<string, () => Promise<{ default: unknown[] }>> = {
    en: () => import('@angular/common/locales/en'),
    de: () => import('@angular/common/locales/de'),
    nl: () => import('@angular/common/locales/nl'),
    fr: () => import('@angular/common/locales/fr'),
    es: () => import('@angular/common/locales/es'),
    pt: () => import('@angular/common/locales/pt'),
    da: () => import('@angular/common/locales/da'),
    pl: () => import('@angular/common/locales/pl'),
    ar: () => import('@angular/common/locales/ar'),
    tr: () => import('@angular/common/locales/tr'),
    nb: () => import('@angular/common/locales/nb'),
    zh: () => import('@angular/common/locales/zh'),
  };

  public setUserLocale(locale: string) {
    if (this.isLocaleAvailable(locale)) {
      this._userLocale.set(locale);
    }
  }

  public setUserTimezone(timezone: string) {
    this._userTimezone.set(timezone);
  }

  public formatDateTimeInUserTimeZone = (dateTime: string): string =>
    formatInTimeZone(fromZonedTime(dateTime, this.userTimezone()), this.userTimezone(), USER_LOCALE_DATE_TIME_FORMAT);

  public setUserZoneOffset(offset: string) {
    this._userZoneOffset.set(offset);
  }

  private isLocaleAvailable(locale: string): boolean {
    return (
      Intl.NumberFormat.supportedLocalesOf([locale]).length > 0 &&
      UserLocaleService.SUPPORTED_LOCALES.includes(locale.split('-')[0])
    );
  }

  convertDateRangeToUserTimeZone(dateRange: DateRange): DateRange {
    return {
      startDate: this.formatDateTimeInUserTimeZone(`${dateRange.startDate}T00:00:00`),
      endDate: this.formatDateTimeInUserTimeZone(`${dateRange.endDate}T23:59:59`),
    };
  }
}
